import React, { useState, useEffect } from 'react';
import { DollarSign, ChevronDown, ChevronUp, Loader2, BarChart2, PieChart as PieIcon, CreditCard, Award, ArrowUpRight, AlertTriangle, Download, ChevronLeft, ChevronRight, Search, Bell, CheckCircle2, Send, Edit, X } from 'lucide-react';
import api from '../../services/api';

function RevenueBillingSection({ financialStats, revenueTimeRange, setRevenueTimeRange, setActiveSection }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [editingCloudCost, setEditingCloudCost] = useState(false);
  const [cloudCostInput, setCloudCostInput] = useState('');
  const [savingCloudCost, setSavingCloudCost] = useState(false);
  const metricsTimeRange = revenueTimeRange || '30d';
  const [stripeTimeRange, setStripeTimeRange] = useState(revenueTimeRange || '30d');
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const transactionsPerPage = 10;

  const [stripeFinancials, setStripeFinancials] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [failedPaymentsData, setFailedPaymentsData] = useState(null);

  // Users Near Plan Limit State
  const [nearLimitUsers, setNearLimitUsers] = useState([]);
  const [loadingNearLimit, setLoadingNearLimit] = useState(true);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [reminderSuccessMap, setReminderSuccessMap] = useState({});
  const [reminderToast, setReminderToast] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoadingNearLimit(true);
    api.get('/super-admin/users-near-limits')
      .then(data => {
        if (isMounted) {
          setNearLimitUsers(data.users || []);
          setLoadingNearLimit(false);
        }
      })
      .catch(err => {
        console.error('Failed to fetch users near plan limit:', err);
        if (isMounted) setLoadingNearLimit(false);
      });
    return () => { isMounted = false; };
  }, []);

  const handleSendReminder = async (user) => {
    setSendingReminderId(user.id);
    try {
      await api.post('/super-admin/send-upgrade-reminder', { userId: user.id });
      const nowIso = new Date().toISOString();
      setReminderSuccessMap(prev => ({ ...prev, [user.id]: nowIso }));
      setReminderToast({
        title: 'Reminder Sent!',
        message: `Upgrade reminder notification sent to ${user.username}.`
      });
      setTimeout(() => {
        setReminderToast(null);
      }, 4000);
    } catch (err) {
      console.error('Failed to send upgrade reminder:', err);
      alert('Failed to send reminder: ' + (err.message || 'Server error'));
    } finally {
      setSendingReminderId(null);
    }
  };

  useEffect(() => {
    let isMounted = true;
    api.get('/super-admin/failed-payments')
      .then(data => {
        if (isMounted) setFailedPaymentsData(data);
      })
      .catch(err => {
        console.error('Failed to fetch failed payments:', err);
      });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setStripeLoading(true);
    api.get(`/super-admin/stripe-financials?timeRange=${stripeTimeRange || '30d'}`)
      .then(data => {
        if (isMounted) {
          setStripeFinancials(data);
          setStripeLoading(false);
        }
      })
      .catch(err => {
        console.error('Failed to fetch Stripe financials:', err);
        if (isMounted) {
          setStripeLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, [stripeTimeRange]);

  useEffect(() => {
    setTransactionsPage(1);
  }, [financialStats?.revenueHistory?.length, revenueTimeRange]);

  const handleStripeTimeRangeChange = (newRange) => {
    setStripeTimeRange(newRange);
  };

  const totalRevenueVal = financialStats?.totalRevenue != null
    ? `$${Number(financialStats.totalRevenue).toFixed(2)}`
    : null;

  // Key Financial Metrics Calculation
  const isLoading = !financialStats;

  // Time Range Label
  const rangeLabels = {
    '7d': 'last 7 days',
    '30d': 'last 30 days',
    'this_month': 'this month',
    'last_month': 'last month',
    'ytd': 'year to date',
    'all_time': 'all time'
  };
  const currentRangeLabel = rangeLabels[metricsTimeRange] || 'selected period';

  // 1. MRR = estimatedMRR (or scaled for metrics time window)
  const baseMRR = financialStats?.estimatedMRR != null
    ? Number(financialStats.estimatedMRR)
    : 0;

  let computedMRR = baseMRR;
  if (financialStats?.totalRevenue != null && financialStats.totalRevenue > 0) {
    if (metricsTimeRange === '7d') {
      computedMRR = (Number(financialStats.totalRevenue) / 7) * 30;
    } else if (metricsTimeRange === 'this_month' || metricsTimeRange === 'last_month' || metricsTimeRange === '30d') {
      computedMRR = Number(financialStats.totalRevenue);
    } else if (metricsTimeRange === 'ytd' || metricsTimeRange === 'all_time') {
      computedMRR = Number(financialStats.totalRevenue) / 12;
    }
  }

  const mrrVal = `$${computedMRR.toFixed(2)}`;

  // 2. ARR = MRR * 12
  const arrVal = `$${(computedMRR * 12).toFixed(2)}`;

  // 3. ARPU
  const arpuNum = financialStats?.arpu != null ? Number(financialStats.arpu) : 0;
  const arpuVal = arpuNum > 0 ? `$${arpuNum.toFixed(2)}` : '$0.00';

  // 4. Churn rate & breakdown
  const churnRateNum = financialStats?.churnRate != null ? Number(financialStats.churnRate) : 0;
  const churnRateVal = `${churnRateNum.toFixed(1)}%`;
  const volChurn = financialStats?.voluntaryChurnRate != null ? `${Number(financialStats.voluntaryChurnRate).toFixed(1)}%` : '0.0%';
  const involChurn = financialStats?.involuntaryChurnRate != null ? `${Number(financialStats.involuntaryChurnRate).toFixed(1)}%` : '0.0%';

  // 5. LTV = ARPU / (churnRate / 100)
  let ltvVal = 'N/A';
  if (arpuNum > 0) {
    if (churnRateNum > 0) {
      const ltvCalc = arpuNum / (churnRateNum / 100);
      ltvVal = `$${ltvCalc.toFixed(2)}`;
    } else {
      ltvVal = `$${(arpuNum * 24).toFixed(2)}`;
    }
  }

  // 6. Profit Margin (requires cloud/hosting cost integration)
  const profitMarginVal = '—';

  // MRR Growth Rate Calculation
  let mrrGrowthRateVal = 'N/A';
  let mrrGrowthColor = 'var(--text-main)';

  const currentTotRev = financialStats?.totalRevenue != null ? Number(financialStats.totalRevenue) : null;
  const prevTotRev = financialStats?.prevTotalRevenue != null ? Number(financialStats.prevTotalRevenue) : null;

  if (currentTotRev != null && prevTotRev != null && !isNaN(currentTotRev) && !isNaN(prevTotRev) && prevTotRev > 0) {
    const growthPct = ((currentTotRev - prevTotRev) / prevTotRev) * 100;
    mrrGrowthRateVal = `${growthPct > 0 ? '+' : ''}${growthPct.toFixed(1)}%`;
    if (growthPct > 0) {
      mrrGrowthColor = '#10b981';
    } else if (growthPct < 0) {
      mrrGrowthColor = '#ef4444';
    }
  }

  // Active Plans Calculation (from subscribersList)
  const subscribersList = financialStats?.subscribersList || [];
  const planSubscriberCounts = {
    hobby: 0,
    pro: 0,
    custom: 0
  };

  subscribersList.forEach((sub) => {
    if (!sub) return;
    const rawPlan = String(sub.plan || sub.tierId || sub.tier || 'hobby').toLowerCase();
    let planKey = 'hobby';
    if (rawPlan.includes('pro')) {
      planKey = 'pro';
    } else if (rawPlan.includes('custom') || rawPlan.includes('custome') || rawPlan.includes('enterprise')) {
      planKey = 'custom';
    } else if (rawPlan.includes('hobby') || rawPlan.includes('free') || rawPlan.includes('basic')) {
      planKey = 'hobby';
    } else if (rawPlan) {
      planKey = rawPlan;
    }
    planSubscriberCounts[planKey] = (planSubscriberCounts[planKey] || 0) + 1;
  });

  const activePlanEntries = Object.keys(planSubscriberCounts).map((planName) => ({
    name: planName,
    count: planSubscriberCounts[planName]
  }));

  // Top Paying Users Calculation (from revenueHistory)
  const revenueHistory = financialStats?.revenueHistory || [];

  const filteredRevenueHistory = revenueHistory.filter((tx) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const userName = String(tx.username || tx.email || tx.user || '').toLowerCase();
    const email = String(tx.email || '').toLowerCase();
    const plan = String(tx.plan || '').toLowerCase();
    const transId = String(tx.transId || tx.id || '').toLowerCase();
    const status = String(tx.status || 'Paid').toLowerCase();
    const date = String(tx.date || '').toLowerCase();
    const price = String(tx.price || tx.amount || '').toLowerCase();

    return (
      userName.includes(term) ||
      email.includes(term) ||
      plan.includes(term) ||
      transId.includes(term) ||
      status.includes(term) ||
      date.includes(term) ||
      price.includes(term)
    );
  });

  const totalTransactionsPages = Math.ceil(filteredRevenueHistory.length / transactionsPerPage) || 1;
  const paginatedRevenueHistory = filteredRevenueHistory.slice(
    (transactionsPage - 1) * transactionsPerPage,
    transactionsPage * transactionsPerPage
  );
  const userSpendingMap = {};

  const handleExportCSV = () => {
    const listToExport = filteredRevenueHistory.length > 0 ? filteredRevenueHistory : revenueHistory;
    if (!listToExport || listToExport.length === 0) return;

    const headers = ['username', 'email', 'plan', 'price', 'transId', 'date', 'status'];
    const csvRows = [headers.join(',')];

    listToExport.forEach((tx) => {
      if (!tx) return;
      const row = [
        `"${String(tx.username || '').replace(/"/g, '""')}"`,
        `"${String(tx.email || '').replace(/"/g, '""')}"`,
        `"${String(tx.plan || '').replace(/"/g, '""')}"`,
        `"${String(tx.price || tx.amount || '').replace(/"/g, '""')}"`,
        `"${String(tx.transId || tx.id || '').replace(/"/g, '""')}"`,
        `"${String(tx.date || '').replace(/"/g, '""')}"`,
        `"${String(tx.status || 'Paid').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'transactions.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  revenueHistory.forEach((tx) => {
    if (!tx) return;
    const userKey = tx.username || tx.email || tx.user || 'Unknown User';
    const priceStr = String(tx.price || tx.amount || '0').replace(/[^0-9.-]+/g, '');
    const priceNum = parseFloat(priceStr) || 0;
    userSpendingMap[userKey] = (userSpendingMap[userKey] || 0) + priceNum;
  });

  const topPayingUsers = Object.keys(userSpendingMap)
    .map((userKey) => ({
      name: userKey,
      totalSpent: userSpendingMap[userKey]
    }))
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 3);

  // Revenue Growth Bar Chart Data Calculation
  const chartData = financialStats?.chartData || [];
  const maxRevenue = Math.max(...chartData.map((d) => Number(d.revenue) || 0), 1);

  // Fully Dynamic Revenue by Plan Calculation
  const planTotals = {};
  let totalPlanRevenue = 0;

  revenueHistory.forEach((tx) => {
    if (!tx) return;
    let planName = tx.plan || 'Other';
    if (planName.toUpperCase().startsWith('CUSTOM_') || planName.toLowerCase().includes('custom')) {
      planName = 'Pro Plan';
    }

    const priceStr = String(tx.price || tx.amount || '0').replace(/[^0-9.-]+/g, '');
    const priceNum = parseFloat(priceStr) || 0;

    planTotals[planName] = (planTotals[planName] || 0) + priceNum;
    totalPlanRevenue += priceNum;
  });

  const paletteColors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#6366f1'];

  const planGroups = Object.keys(planTotals).map((planName, index) => {
    const amount = planTotals[planName] || 0;
    const pct = totalPlanRevenue > 0 ? Math.round((amount / totalPlanRevenue) * 100) : 0;
    return {
      id: planName.toLowerCase(),
      name: planName,
      color: paletteColors[index % paletteColors.length],
      amount,
      pct
    };
  });


  // Build conic-gradient string for solid pie chart
  let gradientStops = [];
  let currentPct = 0;

  // Check if all plans are 0%
  const hasRevenueDistribution = planGroups.some((g) => g.pct > 0);

  if (hasRevenueDistribution) {
    planGroups.forEach((item) => {
      if (item.pct > 0) {
        const nextPct = currentPct + item.pct;
        gradientStops.push(`${item.color} ${currentPct}% ${nextPct}%`);
        currentPct = nextPct;
      }
    });
    if (gradientStops.length > 0 && currentPct < 100) {
      gradientStops[gradientStops.length - 1] = gradientStops[gradientStops.length - 1].replace(/% [0-9]+%$/, `% 100%`);
    }
  }

  const conicGradientStyle = gradientStops.length > 0 ? `conic-gradient(${gradientStops.join(', ')})` : 'none';

  return (
    <div className="sa-section fade-in">
      <div style={{ padding: '0 1.5rem 1.5rem' }}>
        {/* Total Revenue Card */}
        <div className="sa-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div className="sa-infra-icon-wrapper" style={{ background: 'rgba(34, 197, 94, 0.1)', padding: '0.6rem', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DollarSign size={22} style={{ color: '#22c55e' }} />
              </div>
              <div>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>Total Revenue</span>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1.2, marginTop: '0.25rem' }}>
                  {totalRevenueVal !== null ? (
                    totalRevenueVal
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', color: 'var(--text-muted)' }}>
                      <Loader2 size={18} className="sa-spin" /> Loading...
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <select
                className="dashboard-search-input"
                style={{ width: 'auto', padding: '0.45rem 0.85rem', fontSize: '0.85rem', cursor: 'pointer' }}
                value={revenueTimeRange || '30d'}
                onChange={(e) => setRevenueTimeRange && setRevenueTimeRange(e.target.value)}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="this_month">This month</option>
                <option value="last_month">Last month</option>
                <option value="ytd">Year to date</option>
                <option value="all_time">All time</option>
              </select>

              <button
                className="sa-action-btn"
                onClick={() => setShowBreakdown((prev) => !prev)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem', fontSize: '0.85rem' }}
              >
                View breakdown
                {showBreakdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          </div>

          {showBreakdown && (
            <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                {/* Visual 1: Revenue Growth Bar Chart */}
                <div style={{ background: 'var(--surface-hover)', padding: '1.25rem', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                    <BarChart2 size={16} color="var(--primary)" />
                    Revenue Growth
                  </div>

                  {chartData.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '2rem 0', textAlign: 'center' }}>
                      No data for this range
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '140px', paddingTop: '1rem', paddingBottom: '0.25rem' }}>
                      {chartData.map((entry, idx) => {
                        const rev = Number(entry.revenue) || 0;
                        const pct = Math.max(Math.round((rev / maxRevenue) * 100), 4);
                        return (
                          <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                            <div
                              title={`${entry.name}: $${rev.toFixed(2)}`}
                              style={{
                                width: '100%',
                                maxWidth: '28px',
                                height: `${pct}%`,
                                background: 'linear-gradient(180deg, var(--primary) 0%, rgba(59, 130, 246, 0.4) 100%)',
                                borderRadius: '4px 4px 0 0',
                                transition: 'height 0.3s ease'
                              }}
                            />
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.4rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                              {entry.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Visual 2: Revenue by Plan Pie Chart */}
                <div style={{ background: 'var(--surface-hover)', padding: '1.25rem', borderRadius: '12px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                    <PieIcon size={16} color="#10b981" />
                    Revenue by plan
                  </div>

                  {revenueHistory.length === 0 || !hasRevenueDistribution ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '2rem 0', textAlign: 'center' }}>
                      No revenue data yet
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', padding: '0.5rem 0' }}>
                      <div
                        style={{
                          width: '110px',
                          height: '110px',
                          borderRadius: '50%',
                          background: conicGradientStyle,
                          marginBottom: '1rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                      />

                      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', justifyContent: 'center', fontSize: '0.8rem', color: 'var(--text-main)', fontWeight: 500 }}>
                        {planGroups.map((group, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: group.color }} />
                            {group.name} — {group.pct}%
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Key Financial Metrics Grid */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 0 }}>Key Financial Metrics</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Card 1: MRR */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Monthly Recurring Revenue</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                mrrVal || '$0.00'
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>monthly recurring ({currentRangeLabel})</div>
          </div>

          {/* Card 2: ARR */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Annual Recurring Revenue</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                arrVal || '$0.00'
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>annual recurring ({currentRangeLabel})</div>
          </div>

          {/* Card 3: ARPU */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Average Revenue Per User</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                arpuVal || '$0.00'
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>per active user ({currentRangeLabel})</div>
          </div>

          {/* Card 4: Churn Rate */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Churn Rate</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: churnRateNum && churnRateNum > 5 ? '#ef4444' : 'var(--text-main)', marginTop: '0.2rem' }}>
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                churnRateVal || '0.0%'
              )}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {volChurn} voluntary • {involChurn} involuntary
            </div>
          </div>

          {/* Card 5: LTV */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Customer Lifetime Value</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                ltvVal
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>avg. per customer ({currentRangeLabel})</div>
          </div>

          {/* Card 6: Profit Margin */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Profit Margin</div>
              {!editingCloudCost && (
                <button
                  type="button"
                  onClick={() => {
                    setCloudCostInput(financialStats?.monthlyCloudCost != null ? String(financialStats.monthlyCloudCost) : '0');
                    setEditingCloudCost(true);
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary, #3b82f6)', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: 0 }}
                  title="Set or Edit Monthly Cloud Cost"
                >
                  <Edit size={12} /> {financialStats?.monthlyCloudCost ? 'Edit' : 'Set Cost'}
                </button>
              )}
            </div>

            {editingCloudCost ? (
              <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cloudCostInput}
                    onChange={(e) => setCloudCostInput(e.target.value)}
                    placeholder="Cloud cost / mo"
                    style={{ width: '100%', padding: '0.25rem 0.4rem', fontSize: '0.85rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', outline: 'none' }}
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <button
                    type="button"
                    disabled={savingCloudCost}
                    onClick={async () => {
                      setSavingCloudCost(true);
                      try {
                        await api.post('/super-admin/settings', { monthlyCloudCost: parseFloat(cloudCostInput) || 0 });
                        setEditingCloudCost(false);
                        if (typeof window !== 'undefined' && window.location) {
                          window.location.reload();
                        }
                      } catch (err) {
                        alert('Failed to save cloud cost: ' + (err.message || 'Error'));
                      } finally {
                        setSavingCloudCost(false);
                      }
                    }}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', borderRadius: '4px', background: '#10b981', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {savingCloudCost ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingCloudCost(false)}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', borderRadius: '4px', background: 'var(--surface-hover)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: (financialStats?.profitMargin != null && financialStats.profitMargin < 0) ? '#ef4444' : 'var(--text-main)', marginTop: '0.2rem' }}>
                  {isLoading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                      <Loader2 size={14} className="sa-spin" /> Loading...
                    </span>
                  ) : (
                    financialStats?.profitMargin != null ? `${financialStats.profitMargin}%` : '—'
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  {financialStats?.monthlyCloudCost ? `After $${Number(financialStats.monthlyCloudCost).toFixed(2)} cloud cost` : 'No cloud cost set'}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Side-by-side row: Active Plans & Top Paying Users */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
          {/* Card 1: Active Plans */}
          <div className="sa-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)' }}>
                  <CreditCard size={18} color="var(--primary)" />
                  Active plans
                </div>
              </div>

              {activePlanEntries.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1.5rem 0', textAlign: 'center' }}>
                  No active plan data
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                  {activePlanEntries.map((plan, idx) => (
                    <div key={idx} style={{ background: 'var(--surface-hover)', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid var(--border)', flex: '1', minWidth: '90px' }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1 }}>{plan.count}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'capitalize', marginTop: '0.35rem', fontWeight: 600 }}>{plan.name}</div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <button
                  className="sa-action-btn"
                  onClick={() => setActiveSection && setActiveSection('packages')}
                  style={{ width: '100%', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 600 }}
                >
                  Manage pricing & quotas
                  <ArrowUpRight size={15} />
                </button>
              </div>
              {/* Users Near Plan Limit Section */}
              <div style={{ marginTop: '1.25rem', marginBottom: '1.25rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <AlertTriangle size={15} color="#f59e0b" />
                    Users near plan limit
                  </span>
                  {nearLimitUsers.length > 0 && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '10px', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                      {nearLimitUsers.length} Users
                    </span>
                  )}
                </div>

                {loadingNearLimit ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '1rem 0', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                    <Loader2 size={15} className="sa-spin" /> Checking plan limits...
                  </div>
                ) : nearLimitUsers.length === 0 ? (
                  <div style={{ background: 'var(--surface-hover)', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                    All users are within their plan limits ✨
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '220px', overflowY: 'auto' }}>
                    {nearLimitUsers.map((u) => {
                      const lastSent = reminderSuccessMap[u.id] || u.lastReminderSentAt;
                      let isSentRecent = false;
                      if (lastSent) {
                        const sentTime = new Date(lastSent).getTime();
                        const now = Date.now();
                        if (now - sentTime < 24 * 60 * 60 * 1000) {
                          isSentRecent = true;
                        }
                      }

                      const isSending = sendingReminderId === u.id;

                      return (
                        <div key={u.id} style={{ background: 'var(--surface-hover)', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>{u.username}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                              {u.reason}
                            </div>
                          </div>

                          <button
                            onClick={() => handleSendReminder(u)}
                            disabled={isSending || isSentRecent}
                            className="sa-action-btn"
                            title={isSentRecent ? "Reminder already sent within last 24 hours" : "Send upgrade notification alert to user"}
                            style={{
                              fontSize: '0.75rem',
                              padding: '0.35rem 0.65rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              background: isSentRecent ? 'rgba(34, 197, 94, 0.15)' : 'rgba(59, 130, 246, 0.1)',
                              color: isSentRecent ? '#22c55e' : '#3b82f6',
                              borderColor: isSentRecent ? 'rgba(34, 197, 94, 0.3)' : 'rgba(59, 130, 246, 0.3)',
                              cursor: (isSending || isSentRecent) ? 'default' : 'pointer'
                            }}
                          >
                            {isSending ? (
                              <Loader2 size={13} className="sa-spin" />
                            ) : isSentRecent ? (
                              <>
                                <CheckCircle2 size={13} /> Sent (24h Cooldown)
                              </>
                            ) : (
                              <>
                                <Bell size={13} /> Send Reminder
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>


          </div>

          {/* Card 2: Top Paying Users */}
          <div className="sa-card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '1rem' }}>
              <Award size={18} color="#f59e0b" />
              Top paying users
            </div>

            {topPayingUsers.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '2rem 0', textAlign: 'center' }}>
                No transactions yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {topPayingUsers.map((user, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0.85rem', background: 'var(--surface-hover)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 800, width: '22px', height: '22px', borderRadius: '50%', background: index === 0 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.1)', color: index === 0 ? '#f59e0b' : 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        #{index + 1}
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>{user.name}</span>
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-main)' }}>
                      ${user.totalSpent.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stripe Health & MRR Growth Grid */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginTop: '1.5rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>Health & Growth</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <select
              className="dashboard-search-input"
              style={{ width: 'auto', padding: '0.45rem 0.85rem', fontSize: '0.85rem', cursor: 'pointer' }}
              value={stripeTimeRange}
              onChange={(e) => handleStripeTimeRangeChange(e.target.value)}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="ytd">Year to date</option>
              <option value="all_time">All time</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Card 1: MRR growth rate */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Monthly Recurring Revenue growth rate</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: mrrGrowthColor, marginTop: '0.2rem' }}>
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                mrrGrowthRateVal
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>vs previous period ({rangeLabels[stripeTimeRange] || stripeTimeRange})</div>
          </div>

          {/* Card 2: Net vs gross revenue */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Net vs gross revenue</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {stripeLoading || !stripeFinancials ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                `$${Number(stripeFinancials.netRevenue || 0).toFixed(2)}`
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {stripeLoading || !stripeFinancials ? (
                'fetching from Stripe'
              ) : (
                `Gross: $${Number(stripeFinancials.grossRevenue || 0).toFixed(2)}`
              )}
            </div>
          </div>

          {/* Card 3: Stripe processing fees */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Stripe processing fees</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {stripeLoading || !stripeFinancials ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                `$${Number(stripeFinancials.stripeFees || 0).toFixed(2)}`
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {stripeLoading || !stripeFinancials ? (
                'fetching from Stripe'
              ) : (
                stripeFinancials.grossRevenue > 0
                  ? `${((stripeFinancials.stripeFees / stripeFinancials.grossRevenue) * 100).toFixed(1)}% of gross revenue`
                  : '0.0% of gross revenue'
              )}
            </div>
          </div>

          {/* Card 4: Available balance */}
          <div className="sa-infra-card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Available balance</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)', marginTop: '0.2rem' }}>
              {stripeLoading || !stripeFinancials ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={14} className="sa-spin" /> Loading...
                </span>
              ) : (
                `$${Number(stripeFinancials.availableBalance || 0).toFixed(2)}`
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {stripeLoading || !stripeFinancials ? (
                'fetching from Stripe'
              ) : (
                `$${Number(stripeFinancials.pendingBalance || 0).toFixed(2)} pending${stripeFinancials.nextPayoutDate ? ` • Payout ${stripeFinancials.nextPayoutDate}` : ''}`
              )}
            </div>
          </div>
        </div>

        {/* Failed Payments & Dunning Card */}
        <div className="sa-card" style={{ padding: '1.5rem', marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: 700, color: '#ef4444', marginBottom: '1rem' }}>
            <AlertTriangle size={18} color="#ef4444" />
            Failed payments & dunning
          </div>

          {(!failedPaymentsData || failedPaymentsData.totalCount === 0) && (
            <div style={{ background: 'var(--surface-hover)', padding: '0.85rem 1rem', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              No failed payments logged yet — this will populate automatically once payment failures are tracked in the backend
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--surface-hover)', padding: '0.5rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              card declined — {failedPaymentsData ? failedPaymentsData.cardDeclined : '—'}
            </div>
            <div style={{ background: 'var(--surface-hover)', padding: '0.5rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              insufficient funds — {failedPaymentsData ? failedPaymentsData.insufficientFunds : '—'}
            </div>
            <div style={{ background: 'var(--surface-hover)', padding: '0.5rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              expired card — {failedPaymentsData ? failedPaymentsData.expiredCard : '—'}
            </div>
          </div>
        </div>

        {/* Transactions & Invoices Table Card */}
        <div className="sa-card" style={{ padding: '1.5rem', marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>Transactions & invoices</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="dashboard-search-wrapper" style={{ minWidth: '220px', maxWidth: '300px', margin: 0 }}>
                <div className="dashboard-search-icon-box"><Search size={16} /></div>
                <input
                  type="text"
                  className="dashboard-search-input"
                  placeholder="Search plan, user, ID..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setTransactionsPage(1);
                  }}
                  style={{ fontSize: '0.85rem' }}
                />
              </div>
              <button
                className="sa-action-btn"
                onClick={handleExportCSV}
                disabled={!filteredRevenueHistory || filteredRevenueHistory.length === 0}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.45rem 0.85rem',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: (!filteredRevenueHistory || filteredRevenueHistory.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (!filteredRevenueHistory || filteredRevenueHistory.length === 0) ? 0.6 : 1
                }}
              >
                <Download size={15} />
                Export CSV
              </button>
            </div>
          </div>

          <div className="sa-table-wrapper" style={{ margin: 0 }}>
            <table className="sa-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Next Billing Date</th>
                  <th>Status</th>
                  <th>Payment Method</th>
                </tr>
              </thead>
              <tbody>
                {!filteredRevenueHistory || filteredRevenueHistory.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      {searchTerm ? `No transactions match "${searchTerm}"` : 'No transactions yet'}
                    </td>
                  </tr>
                ) : (
                  paginatedRevenueHistory.map((tx, idx) => {
                    const userName = tx.username || tx.email || tx.user || 'Unknown User';
                    let planName = tx.plan || '—';
                    if (planName.toUpperCase().startsWith('CUSTOM_') || planName.toLowerCase().includes('custom')) {
                      planName = 'Pro Plan';
                    }
                    const rawPrice = tx.price || tx.amount || '0';
                    const priceStr = String(rawPrice).trim();
                    const amountFormatted = priceStr.startsWith('$')
                      ? priceStr
                      : `$${(parseFloat(priceStr.replace(/[^0-9.-]+/g, '')) || 0).toFixed(2)}`;
                    const transId = tx.transId || tx.id || '—';
                    const txDate = tx.date || '—';
                    const nextBillingDate = tx.nextBillingDate || (tx.date && tx.date !== '—' ? new Date(new Date(tx.date).getTime() + 30 * 86400000).toLocaleDateString() : 'N/A');
                    const rawStatus = String(tx.status || 'Paid').toLowerCase();
                    const isFailed = rawStatus.includes('fail');
                    const isSuccess = !isFailed && (rawStatus.includes('paid') || rawStatus.includes('succeed') || rawStatus.includes('success'));
                    const statusText = tx.status || (isFailed ? 'Failed' : 'Paid');

                    return (
                      <tr key={tx.transId || idx}>
                        <td style={{ fontWeight: 600, color: 'var(--text-main)' }}>{userName}</td>
                        <td style={{ textTransform: 'capitalize' }}>{planName}</td>
                        <td style={{ fontWeight: 700, color: 'var(--text-main)' }}>{amountFormatted}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{txDate}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{nextBillingDate}</td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.2rem 0.6rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              background: isFailed ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                              color: isFailed ? '#ef4444' : '#22c55e'
                            }}
                          >
                            {statusText}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{tx.paymentMethod || 'No card on file'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredRevenueHistory && filteredRevenueHistory.length > 0 && (
            <div className="analytics-pagination-footer" style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '1rem' }}>
              <div className="analytics-pagination-info">
                Showing {(transactionsPage - 1) * transactionsPerPage + 1} to {Math.min(transactionsPage * transactionsPerPage, filteredRevenueHistory.length)} of {filteredRevenueHistory.length} entries
              </div>
              <div className="analytics-pagination-controls">
                <button
                  className="analytics-page-btn"
                  disabled={transactionsPage === 1}
                  onClick={() => setTransactionsPage((prev) => Math.max(1, prev - 1))}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalTransactionsPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`analytics-page-btn ${transactionsPage === p ? 'active' : ''}`}
                    onClick={() => setTransactionsPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="analytics-page-btn"
                  disabled={transactionsPage === totalTransactionsPages}
                  onClick={() => setTransactionsPage((prev) => Math.min(totalTransactionsPages, prev + 1))}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Success Toast on Top-Right */}
      {reminderToast && (
        <div 
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 999999,
            maxWidth: '390px',
            width: 'calc(100vw - 48px)',
            background: 'linear-gradient(145deg, #131620 0%, #0d0f15 100%)',
            border: '1px solid rgba(34, 197, 94, 0.4)',
            borderRadius: '16px',
            boxShadow: '0 20px 48px rgba(0, 0, 0, 0.8), 0 0 24px rgba(34, 197, 94, 0.25)',
            padding: '1rem 1.2rem',
            backdropFilter: 'blur(20px)',
            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            display: 'flex',
            gap: '0.85rem',
            alignItems: 'flex-start'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.25) 0%, rgba(16, 185, 129, 0.15) 100%)',
            border: '1px solid rgba(34, 197, 94, 0.4)',
            color: '#4ade80',
            padding: '0.55rem',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 16px rgba(34, 197, 94, 0.25)'
          }}>
            <CheckCircle2 size={20} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  boxShadow: '0 0 8px #22c55e'
                }} />
                <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                  {reminderToast.title}
                </h4>
              </div>
              <button 
                onClick={() => setReminderToast(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'; }}
                title="Dismiss"
              >
                <X size={15} />
              </button>
            </div>

            <p style={{
              margin: '0.45rem 0 0 0',
              fontSize: '0.84rem',
              color: '#f1f5f9',
              fontWeight: 450,
              lineHeight: 1.5,
              wordBreak: 'break-word'
            }}>
              {reminderToast.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default RevenueBillingSection;
